// Shared helpers for Airchain's MediaMTX integration -- both ingest (the
// RTMP server/SRT server Wizard tiles, InternalRTMP.js/InternalSRT.js) and
// egress (the RTMP output/SRT output toggles, see restreamer.js's
// UpsertIngest/GetPublicAddress). Core always connects to the servers itself
// rather than the customer/viewer connecting to core directly -- a
// customer never sees or touches these internal addresses. See
// mediamtx_client.py's module docstring (live-captions repo) for the full
// push->pull ingest redesign history and why SRT is the one pull protocol
// used there (PID preservation).
import { AIRCHAIN_TENANT_ID } from './airchain-api';

// The `live/` prefix is not decoration: MediaMTX carries a per-tenant path
// pattern (`~^live/{tenant_id}-.*$`, added by mediamtx_client.py's
// add_tenant_path) and this UI must produce addresses that land inside it.
// Without the prefix the address still *works* -- MediaMTX's `all_others`
// path accepts any publisher -- but it publishes outside the tenant's own
// path, so the tenant's limits and its per-path configuration do not apply
// and the Core, which pulls `live/{tenant}-{channel}-in`, never sees the
// stream at all.
const getStreamPath = (tenantId, channelid) => `live/${tenantId}-${channelid}-in`;

const getRTMPPublishURL = (mediamtx, path) => `rtmp://${mediamtx.mediamtx_public_host}:${mediamtx.mediamtx_public_rtmp_port}/${path}`;

// -- SRT ingest: the streamid shape depends on which server carries it -----
//
// Both servers distinguish publish from read by the streamid, but spell it
// differently, so every ingest SRT address in this file must go through these
// two helpers rather than hardcoding a marker:
//
//   MediaMTX  publish: <path>                    read: read:<path>
//   SRS       publish: <path>  (no marker at all -- SRS's
//             srt_server.default_mode publish makes a short streamid publish)
//             read:    #!::r=<path>,m=request    (explicit, because
//             default_mode publish means a bare read is NOT the default)
//
// The streamid reaches ffmpeg inside a URL. Measured against SRS 8.0.3 with
// ffmpeg as the reader: the RAW '#!::r=<path>,m=request' form connects and
// decodes the stream, while the percent-encoded '%23!::' form fails with an
// I/O error. ffmpeg passes the streamid through undecoded, so '%23' goes on the
// wire literally and SRS -- not recognising the envelope -- treats the read as a
// publish and times it out (error 6002). Do not "fix" this to %23.
//
// That same envelope is why SRS cannot serve a normal viewer: a literal '#'
// starts a URL fragment, so any player that parses the address as a URL drops
// the rest of the streamid before it reaches the wire (SRS then logs the read
// against its default path, live/livestream). Measured: every '#'-free variant
// -- '<path>,m=request', '<path>?m=request', '?streamid=<path>&m=request',
// 'read:<path>' -- is refused. Only ffmpeg-class clients, which pass the
// streamid through verbatim, can read SRS. Hence the split: SRS carries the
// publisher and the core's own reads, while the customer-facing egress is a
// different tier entirely (see below).
const srtBackend = (mediamtx) => (mediamtx && mediamtx.srt_backend) || 'mediamtx';

// SRT is the one protocol whose host can differ from the rest (an SRS relay or
// VPS in front of it); when the controller does not name one, it is the same
// host as everything else.
const srtHost = (mediamtx) => (mediamtx && mediamtx.srt_public_host) || mediamtx.mediamtx_public_host;

const srtPublishStreamId = (mediamtx, path) => (srtBackend(mediamtx) === 'srs' ? path : `publish:${path}`);

const srtReadStreamId = (mediamtx, path) => (srtBackend(mediamtx) === 'srs' ? `#!::r=${path},m=request` : `read:${path}`);

const getSRTPublishURL = (mediamtx, path) => `srt://${srtHost(mediamtx)}:${mediamtx.mediamtx_public_srt_port}?streamid=${srtPublishStreamId(mediamtx, path)}`;

// core's own ingest address -- always SRT, never shown to the customer.
const getReadURL = (mediamtx, path) => `srt://${mediamtx.mediamtx_internal_host}:${mediamtx.mediamtx_internal_srt_port}?streamid=${srtReadStreamId(mediamtx, path)}`;

const isAvailable = (mediamtx) => mediamtx !== null && typeof mediamtx.mediamtx_public_host === 'string' && mediamtx.mediamtx_public_host.length !== 0;

// -- Egress (RTMP output / SRT output) ---------------------------------
//
// A different path from ingest's (-out vs -in) so the two never collide.
// Core pushes its final, already-captioned output here (see restreamer.js's
// UpsertIngest), and the two toggles are genuinely different destinations:
//
//   RTMP output -> MediaMTX, which re-serves the path over RTMP play, SRT read
//                  and HLS -- the convenient, browser-friendly tier.
//   SRT output  -> the SRT egress tier (`srt_egress_backend`), which is the
//                  one that keeps the MPEG-TS program intact.
//
// Measured on the live stack, with a publisher sending program_id 4660 and
// elementary PIDs 0x1e1-0x1e3:
//   SRS       arrives as 4660 / 0x1e1-0x1e3  -- exact, but its reader form is
//             the '#!::' envelope, which ordinary players strip.
//   MediaMTX  arrives as 1 / 0x100-0x102     -- playable, program rewritten.
//   srtrelay  arrives as 4660 / 0x1e1-0x1e3  -- exact AND playable, with plain
//             'publish:<path>' / 'play:<path>' streamids.
// So the SRT egress is what carries PID/PMT/PAT fidelity to a customer, and its
// backend is the controller's `srt_egress_backend` (srtrelay|srs|mediamtx),
// never the ingest's. The host/port come from the pairing the controller serves
// for the public and internal directions.
const getEgressStreamPath = (tenantId, channelid) => `live/${tenantId}-${channelid}-out`;

const srtEgressBackend = (mediamtx) => (mediamtx && mediamtx.srt_egress_backend) || 'mediamtx';

// srtrelay separates mode and name with a SLASH ('publish/<name>' / 'play/<name>'),
// and -- measured, not assumed -- its stream NAME is only the FIRST segment after
// the mode; a third segment is read as a password. So 'live/acme-x-out' would be
// name 'live' with password 'acme-x-out', and every channel in the deployment
// would collapse onto one stream called 'live', the second publisher evicting the
// first (confirmed: two publishers on distinct names coexist, two on distinct
// 'live/...' paths do not). The relay's name therefore drops the Core's internal
// 'live/' prefix and carries the tenant and channel as the first segment, which is
// unique per channel. Its own push and read both use this same name, so nothing
// else in the chain needs to agree with it.
const srtRelayName = (path) => path.replace(/^live\//, '');

const srtEgressStreamId = (mediamtx, path, kind) => {
	const backend = srtEgressBackend(mediamtx);
	if (backend === 'srtrelay') {
		return `${kind === 'publish' ? 'publish' : 'play'}/${srtRelayName(path)}`;
	}
	if (backend === 'srs') {
		return kind === 'publish' ? path : `#!::r=${path},m=request`;
	}
	return kind === 'publish' ? `publish:${path}` : `read:${path}`;
};

const srtEgressURL = (mediamtx, host, port, path, kind) =>
	`srt://${host}:${port}?streamid=${srtEgressStreamId(mediamtx, path, kind)}`;

// core connects OUT to the egress tier to publish -- loopback host, but the
// public port number (the same loopback+public-port pattern ingest uses).
const getEgressSRTPushURL = (mediamtx, path) =>
	srtEgressURL(mediamtx, mediamtx.mediamtx_internal_host, mediamtx.srt_egress_internal_port, path, 'publish');

// The customer's viewer address. For srtrelay and MediaMTX this is an ordinary
// URL any player opens; for SRS it is the envelope, which only ffmpeg-class
// clients handle -- another reason srtrelay is the egress tier of choice.
const getEgressSRTPublicURL = (mediamtx, path) =>
	srtEgressURL(mediamtx, mediamtx.srt_egress_public_host, mediamtx.srt_egress_public_port, path, 'read');

const getEgressPushURL = (mediamtx, path) => `rtmp://${mediamtx.mediamtx_internal_host}:${mediamtx.mediamtx_public_rtmp_port}/${path}`;

// The tee muxer outputs for a channel's restream toggles: one '|'-prefixed
// directive per enabled transport, each with a payload format that matches it --
// SRT carries MPEG-TS, and '-f flv' over an srt:// URL is refused by ffmpeg
// outright, while the RTMP push is flv by definition.
const getEgressPushDirectives = (mediamtx, path, { rtmp, srt } = {}) => {
	const directives = [];
	if (rtmp) {
		directives.push(`|[f=flv]${getEgressPushURL(mediamtx, path)}`);
	}
	if (srt) {
		directives.push(`|[f=mpegts]${getEgressSRTPushURL(mediamtx, path)}`);
	}
	return directives;
};

// Viewer/downstream-facing RTMP read address. RTMP has no distinct publish-vs-play
// URL shape (MediaMTX tells them apart by the RTMP command itself, not the
// address) -- same shape as the ingest publish URL, just the egress path.
const getEgressRTMPPublicURL = (mediamtx, path) => `rtmp://${mediamtx.mediamtx_public_host}:${mediamtx.mediamtx_public_rtmp_port}/${path}`;

export {
	AIRCHAIN_TENANT_ID,
	getStreamPath,
	getRTMPPublishURL,
	getSRTPublishURL,
	getReadURL,
	isAvailable,
	getEgressStreamPath,
	getEgressPushURL,
	getEgressSRTPushURL,
	getEgressPushDirectives,
	getEgressRTMPPublicURL,
	getEgressSRTPublicURL,
};
